import type {SessionResponse} from "@novel/contracts";
import {putOfflineCache,readOfflineCache,removeOfflineCache} from "./draftStore";
let identity:{id:string;offline:boolean}|null=null;
let identityEpoch=0;
let sessionWrite:Promise<void>=Promise.resolve();
let explicitlySignedOut=false;
let signedOutVersion="";
const logoutKey="novel.explicitLogout";
const sessionVersionKey="novel.sessionVersion";
let acceptedVersion="";
function sharedVersion(){try{return localStorage.getItem(sessionVersionKey)??"";}catch{return "";}}
function rotateSessionVersion(){try{localStorage.setItem(sessionVersionKey,crypto.randomUUID());}catch{}}
export const offlineIdentity=()=>!isExplicitlySignedOut()&&acceptedVersion===sharedVersion()?identity:null;
export const sessionEpoch=()=>identityEpoch+":"+sharedVersion();
export function isExplicitlySignedOut(){try{return localStorage.getItem(logoutKey)==="true"||(explicitlySignedOut&&signedOutVersion===sharedVersion());}catch{return explicitlySignedOut;}}
function storeSession(action:()=>Promise<void>){sessionWrite=sessionWrite.catch(()=>undefined).then(action);return sessionWrite;}
export async function verifiedSession(session:SessionResponse,expectedEpoch=sessionEpoch()):Promise<boolean>{
 if(expectedEpoch!==sessionEpoch()||isExplicitlySignedOut())return false;
 if(identity?.id!==session.account.id)identityEpoch++;
 const acceptedEpoch=sessionEpoch();acceptedVersion=sharedVersion();identity={id:session.account.id,offline:false};
 const cacheVersion=acceptedVersion;
 await storeSession(async()=>{if(acceptedEpoch===sessionEpoch())await putOfflineCache("lastVerifiedSession",{...session,cacheVersion});}).catch(()=>undefined);
 if(acceptedEpoch!==sessionEpoch())return false;
 window.dispatchEvent(new Event("novel-session-online"));return true;
}
/** Only an explicit successful login may undo a local logout when the server
 * logout failed offline; focus/refetch must not sign the author back in. */
export async function acceptLogin(session:SessionResponse){
 identityEpoch++;explicitlySignedOut=false;
 rotateSessionVersion();
 try{localStorage.removeItem(logoutKey);}catch{}
 return verifiedSession(session,sessionEpoch());
}
export async function cachedSession(expectedEpoch=sessionEpoch()):Promise<SessionResponse|undefined>{
 if(isExplicitlySignedOut()||expectedEpoch!==sessionEpoch())return;
 const value=await readOfflineCache<SessionResponse&{cacheVersion?:string}>("lastVerifiedSession").catch(()=>undefined);
 if(expectedEpoch!==sessionEpoch()||isExplicitlySignedOut())return;
 if(!value||(!value.cacheVersion?sharedVersion()!=="":value.cacheVersion!==sharedVersion())||!value.account||value.account.status!=="active"||!Number.isFinite(Date.parse(value.expiresAt))||Date.parse(value.expiresAt)<=Date.now()){identity=null;return;}
 acceptedVersion=sharedVersion();identity={id:value.account.id,offline:true};return value;
}
export async function forgetSession(explicit=false){
 const rotate=explicit||offlineIdentity()!==null;
 identityEpoch++;identity=null;if(rotate)rotateSessionVersion();
 if(explicit){explicitlySignedOut=true;signedOutVersion=sharedVersion();try{localStorage.setItem(logoutKey,"true");}catch{}}
 const version=sharedVersion();
 await storeSession(async()=>{if(version===sharedVersion())await removeOfflineCache("lastVerifiedSession");}).catch(()=>undefined);
}
export function cacheableRead(path:string){
 const url=path.split("?")[0]!;
 return url==="/api/works"||/^\/api\/works\/[\da-f-]+(?:\/(?:chapter-directory|chapters(?:\/[\da-f-]+)?|volumes|book-plan|preferences|cursor))?$/.test(url);
}
export async function cacheRead(ownerId:string,path:string,value:unknown){if(cacheableRead(path))await putOfflineCache(ownerId+":read:"+path,value).catch(()=>undefined);}
export async function readCached<T>(ownerId:string,path:string):Promise<T|undefined>{if(!cacheableRead(path))return;return readOfflineCache<T>(ownerId+":read:"+path).catch(()=>undefined);}
export const activeDrafts=new Set<string>();
const activeDraftCounts=new Map<string,number>();
export function retainActiveDraft(key:string):()=>void {
 activeDraftCounts.set(key,(activeDraftCounts.get(key)??0)+1);activeDrafts.add(key);
 let released=false;
 return ()=>{if(released)return;released=true;const count=(activeDraftCounts.get(key)??1)-1;if(count>0)activeDraftCounts.set(key,count);else{activeDraftCounts.delete(key);activeDrafts.delete(key);}};
}
